import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import SearchableDropdown from '../common/SearchableDropdown';
import { XMarkIcon, ScaleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

const LogDisciplineModal = ({
    isOpen,
    onClose,
    onSuccess,
    preselectedCourseId = null,
    customStudentList = null,
    coursesList = []
}) => {
    const { get, post } = useApi();
    const { showToast } = useAppState();
    const queryClient = useQueryClient();

    const [formData, setFormData] = useState({
        student_id: '',
        category_id: '',
        title: '',
        consequences: '',
        severity: 'minor',
        description: '',
        course_id: preselectedCourseId || '',
        owner_ids: []
    });

    useEffect(() => {
        if (isOpen) {
            setFormData(prev => ({
                ...prev,
                course_id: preselectedCourseId || prev.course_id || '',
                student_id: (customStudentList && customStudentList.length === 1) ? customStudentList[0].id : prev.student_id || '',
                title: '',
                consequences: '',
                description: '',
                severity: 'minor',
                owner_ids: []
            }));
        }
    }, [isOpen, preselectedCourseId, customStudentList]);

    // Fetch Active Categories
    const { data: categories = [] } = useQuery({
        queryKey: ['active-discipline-categories'],
        queryFn: async () => {
            const res = await get('/api/discipline/categories/');
            return res.filter(c => c.is_active);
        },
        enabled: isOpen,
        staleTime: 60 * 1000,
    });

    // Default first category if not set
    useEffect(() => {
        if (categories.length > 0 && !formData.category_id) {
            setFormData(prev => ({ ...prev, category_id: categories[0].id }));
        }
    }, [categories, formData.category_id]);

    // Fetch Global Students if customStudentList is not provided
    const { data: globalStudents = [] } = useQuery({
        queryKey: ['discipline-student-options'],
        queryFn: () => get('/api/discipline/records/student-options/'),
        enabled: isOpen && !customStudentList,
        staleTime: 5 * 60 * 1000,
    });

    const students = useMemo(() => {
        if (customStudentList && Array.isArray(customStudentList)) {
            return customStudentList.map(s => ({
                id: s.id,
                name: s.name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || 'Unknown Student',
                student_id: s.student_id || s.admission_number || '',
                grade: s.grade || s.grade_level_name || '',
                stream: s.stream || s.stream_name || ''
            }));
        }
        return globalStudents;
    }, [customStudentList, globalStudents]);

    // Format Categories for SearchableDropdown
    const categoryOptions = useMemo(() => {
        return categories.map(c => ({
            id: c.id,
            name: c.name,
            subtext: `${c.type === 'merit' ? '+' : '-'}${c.points} points (${c.type.toUpperCase()})`,
            type: c.type,
            points: c.points,
            description: `${c.name} ${c.type} ${c.points} pts`,
        }));
    }, [categories]);

    // Format Students for SearchableDropdown
    const studentOptions = useMemo(() => {
        return students.map(s => {
            const gradeInfo = [s.grade, s.stream].filter(Boolean).join(' ');
            return {
                id: s.id,
                name: s.name,
                subtext: gradeInfo || '',
                student_id: s.student_id || '',
                grade: s.grade || '',
                stream: s.stream || '',
                description: `${s.name} ${s.student_id || ''} ${s.grade || ''} ${s.stream || ''}`
            };
        });
    }, [students]);

    const selectedCategory = useMemo(() => {
        return categories.find(c => String(c.id) === String(formData.category_id));
    }, [categories, formData.category_id]);

    const isMerit = selectedCategory?.type === 'merit';

    const logMutation = useMutation({
        mutationFn: (payload) => post('/api/discipline/records/', payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['teacher-discipline-records'] });
            queryClient.invalidateQueries({ queryKey: ['course-discipline-records'] });
            queryClient.invalidateQueries({ queryKey: ['discipline-roster-standing'] });
            queryClient.invalidateQueries({ queryKey: ['hod-discipline-records'] });
            showToast?.(
                isMerit ? 'Merit commendation logged successfully!' : 'Demerit incident recorded successfully.',
                'success'
            );
            onSuccess?.();
            onClose();
        },
        onError: (err) => {
            showToast?.(err.message || 'Failed to record character entry', 'error');
        }
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.student_id) {
            showToast?.('Please select a student', 'error');
            return;
        }
        if (!formData.category_id) {
            showToast?.('Please select a category', 'error');
            return;
        }
        if (isMerit && !formData.title.trim()) {
            showToast?.('Please enter a title for this merit commendation', 'error');
            return;
        }
        if (!formData.description.trim()) {
            showToast?.(isMerit ? 'Please provide commendation notes' : 'Please provide offense details', 'error');
            return;
        }
        if (!isMerit && !formData.consequences.trim()) {
            showToast?.('Please specify the consequences administered for this offense', 'error');
            return;
        }

        const payload = {
            student: parseInt(formData.student_id),
            category: parseInt(formData.category_id),
            title: isMerit ? formData.title.trim() : null,
            consequences: isMerit ? null : formData.consequences.trim(),
            severity: isMerit ? null : formData.severity,
            course: formData.course_id ? parseInt(formData.course_id) : (preselectedCourseId ? parseInt(preselectedCourseId) : null),
            learning_area: formData.course_id ? parseInt(formData.course_id) : (preselectedCourseId ? parseInt(preselectedCourseId) : null),
            description: formData.description.trim()
        };

        logMutation.mutate(payload);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn overflow-hidden">
            <div className="bg-white rounded-[2.5rem] w-full max-w-3xl shadow-2xl border border-slate-100 overflow-hidden flex flex-col max-h-[88vh]">
                
                {/* Clean White Header Without Badge */}
                <div className="px-8 py-6 bg-white border-b border-slate-100 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-2xl bg-[#18216D]/5 text-[#18216D]">
                            <ScaleIcon className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-xl font-black text-[#18216D] uppercase tracking-tight">
                                Log Merit / Demerit
                            </h3>
                            <p className="text-slate-500 text-xs font-medium mt-0.5">
                                Capture student merits or behavioral infractions with structured follow-up governance.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-700 p-2 rounded-full hover:bg-slate-100 transition-colors"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Form with Scoped Custom Scrollbar */}
                <form onSubmit={handleSubmit} className="p-8 space-y-6 flex-1 overflow-y-auto custom-scrollbar">
                    
                    {/* Merit Title / Headline (Only for Merits) */}
                    {isMerit && (
                        <div>
                            <label className="block text-xs font-black uppercase tracking-wider text-[#18216D] mb-2">
                                Title / Headline *
                            </label>
                            <input
                                type="text"
                                required
                                value={formData.title}
                                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="e.g. Exemplary Class Participation, Punctuality Commendation, Leadership Praise..."
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold text-slate-700 outline-none focus:bg-white focus:border-[#18216D] transition-all"
                            />
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Searchable Category Selection */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-xs font-black uppercase tracking-wider text-[#18216D]">
                                    Category Classification *
                                </label>
                                {selectedCategory && (
                                    <span className={`text-[11px] font-black ${isMerit ? 'text-emerald-600' : 'text-rose-600'}`}>
                                        {isMerit ? `+${selectedCategory.points} pts` : `-${selectedCategory.points} pts`}
                                    </span>
                                )}
                            </div>
                            <SearchableDropdown
                                options={categoryOptions}
                                selectedValues={formData.category_id ? [formData.category_id] : []}
                                onChange={(val) => setFormData(prev => ({ ...prev, category_id: val }))}
                                placeholder="Search category by name or type..."
                                subDisplayKey="subtext"
                                searchKeys={['name', 'description']}
                            />
                        </div>

                        {/* Searchable Scholar Selection */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-xs font-black uppercase tracking-wider text-[#18216D]">
                                    Scholar *
                                </label>
                                {customStudentList && (
                                    <span className="text-[10px] font-black uppercase text-emerald-600 tracking-wider">
                                        Enrolled in Class ({students.length})
                                    </span>
                                )}
                            </div>
                            <SearchableDropdown
                                options={studentOptions}
                                selectedValues={formData.student_id ? [formData.student_id] : []}
                                onChange={(val) => setFormData(prev => ({ ...prev, student_id: val }))}
                                placeholder="Search scholar by name..."
                                subDisplayKey="subtext"
                                searchKeys={['name', 'student_id', 'grade', 'stream']}
                            />
                        </div>
                    </div>

                    {/* Learning Area / Course (if not preselected and coursesList provided) */}
                    {!preselectedCourseId && coursesList.length > 0 && (
                        <div>
                            <label className="block text-xs font-black uppercase tracking-wider text-[#18216D] mb-2">
                                Class / Learning Area Context (Optional)
                            </label>
                            <select
                                value={formData.course_id}
                                onChange={(e) => setFormData({ ...formData, course_id: e.target.value })}
                                className="w-full px-4 py-3 text-xs font-bold bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-[#18216D] outline-none"
                            >
                                <option value="">-- School-Wide / General --</option>
                                {coursesList.map(c => (
                                    <option key={c.id} value={c.id}>
                                        {c.name} {c.grade_level_name ? `(${c.grade_level_name} ${c.stream_display_name || ''})` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Severity (only for demerits) */}
                    {!isMerit && (
                        <div>
                            <label className="block text-xs font-black uppercase tracking-wider text-[#18216D] mb-2">
                                Infraction Severity Level *
                            </label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {[
                                    { value: 'minor', label: 'Minor', color: 'border-slate-200 hover:border-slate-400' },
                                    { value: 'moderate', label: 'Moderate', color: 'border-amber-200 hover:border-amber-400' },
                                    { value: 'major', label: 'Major', color: 'border-orange-200 hover:border-orange-400' },
                                    { value: 'severe', label: 'Severe', color: 'border-rose-300 hover:border-rose-500' }
                                ].map(sev => (
                                    <button
                                        type="button"
                                        key={sev.value}
                                        onClick={() => setFormData({ ...formData, severity: sev.value })}
                                        className={`py-2.5 px-3 rounded-xl border text-xs font-black uppercase tracking-wider transition-all ${
                                            formData.severity === sev.value
                                                ? 'bg-[#18216D] text-white border-[#18216D] shadow-sm scale-[1.02]'
                                                : `bg-slate-50 text-slate-600 ${sev.color}`
                                        }`}
                                    >
                                        {sev.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Class Teacher Auto-Routing Notice for Major/Severe Infractions */}
                    {!isMerit && ['major', 'severe'].includes(formData.severity) && (
                        <div className="p-4 bg-indigo-50/70 border border-indigo-100 rounded-2xl flex items-start gap-3.5">
                            <div className="p-2 bg-[#18216D] text-white rounded-xl shrink-0 mt-0.5 shadow-sm">
                                <ShieldCheckIcon className="w-4 h-4" />
                            </div>
                            <div>
                                <p className="text-xs font-black text-[#18216D] uppercase tracking-wide">
                                    Class Teacher Review Required
                                </p>
                                <p className="text-xs font-medium text-slate-600 mt-0.5 leading-relaxed">
                                    This {formData.severity} infraction will automatically route to the student's Stream Class Teacher for investigation, active handling, and escalation if required.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Description / Notes (Offense for demerit, Commendation Notes for merit) */}
                    <div>
                        <label className="block text-xs font-black uppercase tracking-wider text-[#18216D] mb-2">
                            {isMerit ? 'Commendation Notes & Observations *' : 'Offense / Infraction Details *'}
                        </label>
                        <textarea
                            required
                            rows={3}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder={isMerit ? "Provide context and rationale for this commendation..." : "Describe the offense, context, and what happened..."}
                            className="w-full px-4 py-3 text-xs font-bold bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:border-[#18216D] outline-none"
                        />
                    </div>

                    {/* Consequences to the Offense (Only for demerits) */}
                    {!isMerit && (
                        <div>
                            <label className="block text-xs font-black uppercase tracking-wider text-[#18216D] mb-2">
                                Consequences to the Offense *
                            </label>
                            <textarea
                                required
                                rows={2}
                                value={formData.consequences}
                                onChange={(e) => setFormData({ ...formData, consequences: e.target.value })}
                                placeholder="Specify the consequences administered (e.g. Verbal reprimand, 1-hour detention, apology letter, parent conference scheduled)..."
                                className="w-full px-4 py-3 text-xs font-bold bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:border-[#18216D] outline-none"
                            />
                        </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-3 text-xs font-bold text-slate-500 hover:text-slate-800 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={logMutation.isPending}
                            className={`px-8 py-3 text-white rounded-2xl text-xs font-black uppercase tracking-wider transition-all shadow-lg active:scale-95 disabled:opacity-50 ${
                                isMerit ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20' : 'bg-[#18216D] hover:bg-[#0D164F] shadow-indigo-900/20'
                            }`}
                        >
                            {logMutation.isPending ? 'Recording...' : isMerit ? 'Record Merit' : 'Log Incident'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LogDisciplineModal;
